/* client/src/dnd/components/IconUpload.js */
import React, { useState, useEffect } from 'react';

async function fetchJSON(url, opts = {}) {
  const res = await fetch(url, { credentials: 'include', ...opts });
  let body = null;
  try {
    body = await res.json();
  } catch {}
  if (!res.ok) {
    const msg = body?.error || body?.message || `${res.status} ${res.statusText}`;
    const e = new Error(msg);
    e.status = res.status;
    e.body = body;
    throw e;
  }
  return body;
}

function cacheBust(url, seed) {
  if (!url) return url;
  const u = String(url);
  const sep = u.includes('?') ? '&' : '?';
  const t = typeof seed === 'number' ? seed : Date.now();
  return `${u}${sep}t=${t}`;
}

export default function IconUpload({
  initialIconUrl = '',
  mapId,
  shapeId = null,
  typeId = null,
  onIconUploaded = () => {},
  disabled = false,
}) {
  const [iconFile, setIconFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [iconUrl, setIconUrl] = useState(initialIconUrl);

  const [existingIcons, setExistingIcons] = useState([]);
  const [iconsLoading, setIconsLoading] = useState(false);
  const [iconsError, setIconsError] = useState('');
  const [selectedExistingId, setSelectedExistingId] = useState('');

  useEffect(() => {
    setIconUrl(initialIconUrl);
  }, [initialIconUrl]);

  const mode = shapeId ? 'shape' : typeId ? 'type' : null;

  const uploadUrl =
    mode === 'shape'
      ? null
      : mode === 'type'
      ? `/api/type-edit/${typeId}/icon`
      : null;

  const deleteUrl = mode === 'type' ? uploadUrl : null;

  const title =
    mode === 'shape'
      ? 'Upload Token Icon (PNG, max 64x64, 256KB)'
      : 'Upload Type Icon (PNG, max 64x64, 256KB)';

  useEffect(() => {
    if (!mapId) return;
    let cancelled = false;

    async function loadIcons() {
      setIconsLoading(true);
      setIconsError('');
      try {
        const typeData = await fetchJSON(`/api/type-edit/map/${mapId}/icons`).catch(() => null);
        if (cancelled) return;

        const typeIcons = Array.isArray(typeData?.icons) ? typeData.icons : [];
        setExistingIcons(typeIcons);

        if (typeIcons.length > 0) {
          const match = typeIcons.find(i => i.icon_file === initialIconUrl);
          const chosen = match ?? typeIcons[0];
          setSelectedExistingId(String(chosen.id ?? chosen.icon_file));
        } else {
          setSelectedExistingId('');
        }

        if (!typeData) {
          setIconsError('Failed to load existing icons');
        }
      } catch (e) {
        if (!cancelled) {
          setIconsError(e.message || 'Failed to load existing icons');
          setExistingIcons([]);
          setSelectedExistingId('');
        }
      } finally {
        if (!cancelled) setIconsLoading(false);
      }
    }

    loadIcons();
    return () => {
      cancelled = true;
    };
  }, [mapId, initialIconUrl]);

  const handleIconUpload = async (file) => {
    setError('');
    setMessage('');
    if (!file) return;

    if (!mode) {
      setError('Save first before adding an icon.');
      return;
    }

    if (file.type !== 'image/png') {
      setError('Only PNG files allowed');
      return;
    }
    if (file.size > 256 * 1024) {
      setError('Max 256KB size');
      return;
    }

    const img = new window.Image();
    img.src = URL.createObjectURL(file);

    img.onload = async function () {
      if (img.width > 64 || img.height > 64) {
        setError('Icon must be 64x64px or less.');
        return;
      }

      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('icon', file);
        if (mapId != null) formData.append('map_id', String(mapId));

        let data;

        if (mode === 'type') {
          if (!uploadUrl) {
            throw new Error('Invalid upload target');
          }
          const res = await fetch(uploadUrl, {
            method: 'POST',
            body: formData,
            credentials: 'include',
          });
          data = await res.json();
          if (!res.ok || !data.icon_url) {
            throw new Error(data.error || 'Icon upload failed');
          }
        } else if (mode === 'shape') {
          if (!mapId) {
            throw new Error('Missing mapId for upload');
          }
          data = await fetchJSON(`/api/type-edit/map/${mapId}/icon-upload`, {
            method: 'POST',
            body: formData,
          });
          if (!data.icon_url) {
            throw new Error('Icon upload failed');
          }
        } else {
          throw new Error('No valid upload target configured');
        }

        const busted = cacheBust(data.icon_url);
        setIconUrl(busted);
        setMessage('Icon uploaded!');
        onIconUploaded(busted);

        setExistingIcons(prev => {
          if (!data.icon_url) return prev;
          if (prev.some(i => i.icon_file === data.icon_url)) return prev;
          const name =
            file.name && typeof file.name === 'string'
              ? file.name.replace(/\.[^.]+$/, '')
              : 'Icon';
          return [...prev, { id: null, name, icon_file: data.icon_url }];
        });
      } catch (err) {
        setError(err.message || 'Failed to upload icon.');
      } finally {
        setUploading(false);
        setIconFile(null);
      }
    };
  };

  const handleRemoveIcon = async () => {
    if (!window.confirm('Remove this icon?')) return;

    if (!mode) {
      return;
    }

    if (mode === 'shape') {
      setError('');
      setMessage('Icon removed.');
      setIconUrl('');
      onIconUploaded('');
      return;
    }

    if (!deleteUrl) return;

    setUploading(true);
    setError('');
    setMessage('');

    try {
      const res = await fetch(deleteUrl, {
        method: 'DELETE',
        credentials: 'include',
      });

      const data = await res.json();
      if (!res.ok || !(data.ok || data.success)) {
        throw new Error(data.error || 'Remove icon failed.');
      }

      setIconUrl('');
      setMessage('Icon removed.');
      onIconUploaded('');
    } catch (err) {
      setError(err.message || 'Failed to remove icon.');
    } finally {
      setUploading(false);
    }
  };

  const selectedExisting = existingIcons.find(
    i => String(i.id ?? i.icon_file) === String(selectedExistingId)
  );

  const handleUseExisting = async () => {
    setError('');
    setMessage('');
    if (!mode) {
      setError('Save first before adding an icon.');
      return;
    }
    if (!selectedExisting || !selectedExisting.icon_file) {
      setError('Invalid icon selection');
      return;
    }
    const rawUrl = selectedExisting.icon_file;
    const busted = cacheBust(rawUrl);

    try {
      if (mode === 'type') {
        await fetchJSON(`/api/type-edit/${typeId}/icon-assign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ icon_url: rawUrl }),
        });
        setMessage('Icon assigned from existing icons');
      } else if (mode === 'shape') {
        setMessage('Icon selected');
      } else {
        throw new Error('No valid assign target configured');
      }

      setIconUrl(busted);
      onIconUploaded(busted);
    } catch (e) {
      setError(e.message || 'Failed to assign existing icon');
    }
  };

  return (
    <div
      style={{
        background: '#232427',
        borderRadius: 12,
        padding: 24,
        minWidth: 340,
        marginTop: 10,
        marginBottom: 10,
        boxShadow: '0 2px 20px #000c',
        color: '#fff',
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 14 }}>
        {title}
      </div>

      <div
        style={{
          border: '1px solid #3a3a3a',
          borderRadius: 8,
          padding: 10,
          marginBottom: 12,
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
          Use Existing Icon
        </div>
        {iconsLoading ? (
          <div style={{ fontSize: 13, color: '#9aa0a6' }}>Loading icons…</div>
        ) : existingIcons.length === 0 ? (
          <div style={{ fontSize: 13, color: '#9aa0a6' }}>
            No existing icons found for this map.
          </div>
        ) : (
          <>
            <select
              value={selectedExistingId}
              onChange={e => setSelectedExistingId(e.target.value)}
              style={{
                width: '100%',
                background: '#333',
                color: '#fff',
                border: '1px solid #444',
                borderRadius: 6,
                padding: '6px 10px',
                marginBottom: 8,
              }}
              disabled={uploading || disabled}
            >
              {existingIcons.map(icon => (
                <option
                  key={String(icon.id ?? icon.icon_file)}
                  value={String(icon.id ?? icon.icon_file)}
                >
                  {icon.name ? icon.name : 'Icon'}
                </option>
              ))}
            </select>
            {selectedExisting && selectedExisting.icon_file && (
              <div style={{ marginBottom: 8 }}>
                <img
                  src={cacheBust(selectedExisting.icon_file)}
                  alt="Selected Icon Preview"
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 6,
                    border: '1px solid #444',
                    background: '#111',
                  }}
                  onError={e => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}
            <button
              onClick={handleUseExisting}
              disabled={!selectedExistingId || uploading || disabled}
              style={{
                background: '#4cc153',
                color: '#101314',
                border: 'none',
                borderRadius: 7,
                padding: '6px 14px',
                fontWeight: 700,
                fontSize: 14,
                cursor:
                  selectedExistingId && !uploading && !disabled
                    ? 'pointer'
                    : 'not-allowed',
              }}
            >
              Use Selected Icon
            </button>
          </>
        )}
        {iconsError && (
          <div style={{ marginTop: 6, fontSize: 12, color: '#f44c2b' }}>{iconsError}</div>
        )}
      </div>

      <input
        type="file"
        accept="image/png"
        disabled={uploading || disabled}
        onChange={(e) => setIconFile(e.target.files[0] || null)}
        style={{ marginBottom: 8 }}
      />

      {iconFile && (
        <div style={{ marginBottom: 8 }}>
          Selected: {iconFile.name} ({Math.round(iconFile.size / 1024)} KB)
        </div>
      )}

      {error && <div style={{ color: '#f44c2b', marginBottom: 8 }}>{error}</div>}
      {message && <div style={{ color: '#81ff9c', marginBottom: 8 }}>{message}</div>}

      <div style={{ display: 'flex', gap: 12 }}>
        <button
          onClick={() => iconFile && handleIconUpload(iconFile)}
          disabled={!iconFile || uploading || disabled}
          style={{
            background: '#4cc153',
            color: '#fff',
            border: 'none',
            borderRadius: 7,
            padding: '7px 18px',
            fontWeight: 700,
            fontSize: 15,
            cursor:
              iconFile && !uploading && !disabled ? 'pointer' : 'not-allowed',
          }}
        >
          Upload
        </button>

        <button
          onClick={() => {
            setIconFile(null);
            setError('');
            setMessage('');
          }}
          disabled={uploading || disabled}
          style={{
            background: '#333',
            color: '#fff',
            border: 'none',
            borderRadius: 7,
            padding: '7px 18px',
            fontWeight: 700,
            fontSize: 15,
            cursor: !uploading && !disabled ? 'pointer' : 'not-allowed',
          }}
        >
          Cancel
        </button>

        {iconUrl && (
          <button
            onClick={handleRemoveIcon}
            disabled={uploading || disabled}
            style={{
              background: '#9a2121',
              color: '#fff',
              border: 'none',
              borderRadius: 7,
              padding: '7px 18px',
              fontWeight: 700,
              fontSize: 15,
              cursor: !uploading && !disabled ? 'pointer' : 'not-allowed',
            }}
          >
            Remove
          </button>
        )}
      </div>

      {iconUrl && (
        <div style={{ marginTop: 10 }}>
          <img
            src={cacheBust(iconUrl)}
            style={{
              width: 64,
              height: 64,
              borderRadius: 4,
              border: '1px solid #888',
              objectFit: 'contain',
              background: '#000',
            }}
            alt="icon"
            onError={(e) => (e.currentTarget.style.display = 'none')}
          />
        </div>
      )}
    </div>
  );
}
